import {Link} from '@github-ui/react-router'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {useAnalytics} from '@github-ui/use-analytics'
import {Button} from '@primer/react'
import {Card} from '@primer/react/experimental'
import type {Ref} from 'react'
import {useEffect, useRef, useState} from 'react'

import {useNewPropertyPath} from '../hooks/use-property-details-paths'
import type {DefinitionIceBreakerProps} from './definition-ice-breaker-suggestions'
import styles from './DefinitionIceBreakers.module.css'

const DISPLAYED_SUGGESTIONS_COUNT = 3

interface DefinitionIceBreakersProps {
  suggestions: DefinitionIceBreakerProps[]
}

export function DefinitionIceBreakers({suggestions}: DefinitionIceBreakersProps) {
  const {sendAnalyticsEvent} = useAnalytics()
  const hasTrackedShownEventRef = useRef(false)
  const [page, setPage] = useState(0)

  // When the final "See more" click removes the button, move focus to the first card so keyboard
  // users aren't dropped to the document body.
  const firstCardRef = useRef<HTMLAnchorElement>(null)

  // Suggestions are shuffled upstream (once per browser load) and passed in via props. We render nothing on the server
  // (and during hydration) to avoid SSR/hydration mismatches and an initial paint that immediately reorders the list.
  const {isBrowser} = useRuntimeEnvironment()

  useEffect(() => {
    if (hasTrackedShownEventRef.current) return
    hasTrackedShownEventRef.current = true
    sendAnalyticsEvent('repo_custom_properties_suggestions.icebreakers.shown')
  }, [sendAnalyticsEvent])

  const isPaginated = suggestions.length > DISPLAYED_SUGGESTIONS_COUNT
  const hasMore = (page + 1) * DISPLAYED_SUGGESTIONS_COUNT < suggestions.length

  useEffect(() => {
    if (!isPaginated || hasMore) return
    firstCardRef.current?.focus()
  }, [isPaginated, hasMore])

  if (!isBrowser) return null

  const start = page * DISPLAYED_SUGGESTIONS_COUNT
  const displayedSuggestions = suggestions.slice(start, start + DISPLAYED_SUGGESTIONS_COUNT)

  const onSeeMoreClick = () => {
    setPage(previousPage => previousPage + 1)
    sendAnalyticsEvent('repo_custom_properties_suggestions.icebreakers.see_more_clicked')
  }

  return (
    <div className="flex-1 px-3">
      <div className="d-flex flex-items-center flex-justify-between mb-4">
        <h3 className="h4">Suggested custom properties</h3>
        {hasMore && (
          <Button type="button" onClick={onSeeMoreClick} aria-label="See more suggested properties">
            See more
          </Button>
        )}
      </div>
      <div className="d-flex gap-3 flex-wrap">
        {displayedSuggestions.map((suggestion, index) => (
          <DefinitionIceBreaker
            key={suggestion.suggestedDefinition.propertyName}
            ref={index === 0 ? firstCardRef : undefined}
            {...suggestion}
          />
        ))}
      </div>
    </div>
  )
}

function DefinitionIceBreaker({
  description,
  suggestedDefinition,
  ref,
}: DefinitionIceBreakerProps & {ref?: Ref<HTMLAnchorElement>}) {
  const newPropertyPath = useNewPropertyPath()
  const {sendAnalyticsEvent} = useAnalytics()

  return (
    <Link
      ref={ref}
      to={newPropertyPath}
      state={{suggestedDefinition}}
      className={styles.Link}
      onClick={() =>
        sendAnalyticsEvent('repo_custom_properties_suggestions.icebreakers.clicked', undefined, {
          propertyName: suggestedDefinition.propertyName,
        })
      }
    >
      <Card className={styles.Card} data-testid="definition-ice-breaker">
        <Card.Heading as="h4" className={styles.CardHeading}>
          {suggestedDefinition.propertyName}
        </Card.Heading>
        <Card.Description>{description}</Card.Description>
      </Card>
    </Link>
  )
}
